<template>
  <div id="page-focus-target" class="hide-focus">
    <v-row>
      <v-col cols="12">
        <gg-card-title class="pb-6 pt-0">
          Migration Request
        </gg-card-title>
        <gg-card>
          <gg-card-header>
            <template v-if="step_counter === 1">
              Let’s begin your migration!
            </template>
            <template v-else>
              Provide your Migration Details
            </template>
          </gg-card-header>
          <gg-card-text>
            <v-row justify="center" no-gutters>
              <v-col cols="12" md="11">
                <v-stepper v-model="step_counter">
                  <v-stepper-header>
                    <v-stepper-step :complete="step_counter > 1" step="1">
                      Migration Request: Introduction
                    </v-stepper-step>
                    <v-divider></v-divider>
                    <v-stepper-step :complete="step_counter > 2" step="2">
                      Migration Request: Details
                    </v-stepper-step>
                  </v-stepper-header>
                  <v-stepper-items>
                    <!-- step 1 -->
                    <v-stepper-content step="1">
                      <v-card class="app-card card-borderless">
                        <v-card-text>
                          <v-form data-vv-scope="step1" @submit.prevent="">
                            <p class="body-1">{{ step_one.title }}</p>
                            <gg-select
                              label="Select a service"
                              aria-label="Select a service"
                              outlined
                              :items="servicesList"
                              name="Service"
                              item-text="domain"
                              item-value="id"
                              v-model="step_one.model.hosting_id"
                              v-validate="'required'"
                              :error-messages="errors.collect('step1.Service')"
                            >
                              <template slot="item" slot-scope="{ item }">
                                {{ item.name }}: {{ item.domain }}
                              </template>
                            </gg-select>
                            <gg-btn color="primary"  tile @click="moveToStep(2, 'step1')">
                              Continue
                            </gg-btn>
                          </v-form>
                        </v-card-text>
                      </v-card>
                    </v-stepper-content>
                    <!-- step 2 -->
                    <v-stepper-content step="2">
                      <v-card class="app-card card-borderless">
                        <gg-card-title subtitle class="pb-6">
                          Migration Instructions &amp; Details
                        </gg-card-title>
                        <p class="mb-6">
                          Now we need some information about your previous provider. Answer as accurately
                          as possible to expedite your request.<br/><br/>
                          Don't know everything? No problem! Just tell us what you can, and we will be in touch
                          if we have any questions.<br/><br/>

                          In most cases it is ideal to provide us with your primary control panel or account
                          management login. This will usually give us everything we need to complete your migration.
                          You can provide us with your FTP information instead if you are just transferring a
                          website. Use the Additional Instructions field to supply additional login information
                          and/or other instructions for us.<br/><br/>

                          More information about migrating to GreenGeeks is available in our
                          <a href="https://www.greengeeks.com/kb/2498/greengeeks-migration-policy/"
                             target="_blank">
                            Migration Policy
                          </a>.
                        </p>
                        <v-form @submit.prevent="">
                          <v-row>
                            <v-col cols="12" sm="6" md="4" class="py-0">
                              <gg-text-field
                                outlined
                                label="Previous Hosting Provider"
                                aria-label="Previous Hosting Provider"
                                v-model="step_two.model.previous_provider"
                                v-validate="rules.provider"
                                data-vv-name="provider"
                                data-vv-as="Previous Hosting Provider"
                                :error-messages="errors.collect('provider')"
                              ></gg-text-field>
                            </v-col>
                            <v-col cols="12" sm="6" md="4" class="py-0">
                              <gg-text-field
                                outlined
                                label="Control Panel URL"
                                aria-label="Control Panel URL"
                                v-model="step_two.model.cp_url"
                                v-validate="rules.cp_url"
                                data-vv-name="cp_url"
                                data-vv-as="Control Panel URL"
                                :error-messages="errors.collect('cp_url')"
                              ></gg-text-field>
                            </v-col>
                            <v-col cols="12" sm="6" md="4" class="py-0">
                              <gg-select
                                outlined
                                label="Control Panel Type"
                                aria-label="Control Panel Type"
                                v-model="step_two.model.cp_type"
                                v-validate="rules.cp_type"
                                data-vv-name="cp_type"
                                data-vv-as="Control Panel Type"
                                :error-messages="errors.collect('cp_type')"
                                :items="step_two.cp_items"
                                item-text="name"
                                item-value="value"
                              ></gg-select>
                            </v-col>
                            <v-col cols="12" sm="6" md="4" class="py-0">
                              <gg-text-field
                                outlined
                                label="FTP Hostname"
                                aria-label="FTP Hostname"
                                v-model="step_two.model.hostname"
                                v-validate="rules.hostname"
                                data-vv-name="hostname"
                                data-vv-as="FTP Hostname"
                                :error-messages="errors.collect('hostname')"
                              ></gg-text-field>
                            </v-col>
                            <v-col cols="12" sm="6" md="4" class="py-0">
                              <gg-text-field
                                outlined
                                label="Control Panel/FTP Username"
                                aria-label="Control Panel/FTP Username"
                                v-model="step_two.model.username"
                                v-validate="rules.username"
                                data-vv-name="username"
                                data-vv-as="Control Panel/FTP Username"
                                :error-messages="errors.collect('username')"
                              ></gg-text-field>
                            </v-col>
                            <v-col cols="12" sm="6" md="4" class="py-0">
                              <gg-text-field
                                outlined
                                label="Control Panel/FTP Password"
                                aria-label="Control Panel/FTP Password"
                                v-model="step_two.model.password"
                                v-validate="rules.password"
                                data-vv-name="password"
                                data-vv-as="Control Panel/FTP Password"
                                :error-messages="errors.collect('password')"
                              ></gg-text-field>
                            </v-col>
                            <v-col cols="12" class="py-0">
                              <gg-input-label>Comments</gg-input-label>
                              <v-textarea
                                :placeholder="'Additional Instructions or Comments (for example: additional ' +
                                'login information, steps to login, etc)'"
                                outlined
                                class="gg-input"
                                v-model="step_two.model.notes"
                              ></v-textarea>
                            </v-col>
                            <v-col cols="12" class="py-0">
                              <v-checkbox
                                v-model="step_two.model.thirdpartymail"
                                :true-value="1"
                                :false-value="0"
                                color="primary"
                              >
                                <template v-slot:label>
                                  <label class="v-label" style="cursor:pointer;" aria-hidden="true">
                                    I use a third party email service and I would like to keep them as our mail host.
                                  </label>
                                </template>
                              </v-checkbox>
                            </v-col>
                            <v-col cols="12" class="pt-0">
                              <gg-btn
                                color="primary"
                                tile
                                :loading="loading"
                                @click="submitForm"
                              >
                                Submit
                              </gg-btn>
                              <gg-btn
                                tile
                                text
                                color="primary"
                                @click="step_counter = 1"
                              >
                                Back
                              </gg-btn>
                            </v-col>
                          </v-row>
                        </v-form>
                      </v-card>
                    </v-stepper-content>
                  </v-stepper-items>
                </v-stepper>
              </v-col>
            </v-row>
          </gg-card-text>
        </gg-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { resetFocus } from 'oaf-side-effects'
import amapi from '@/api/amapi'

export default {
  name: 'Migration',
  data: () => ({
    showGroups: ['Shared Hosting', 'Reseller Hosting', 'Virtual Private Server'],
    step_counter: 1,
    step_one: {
      model: {
        hosting_id: ''
      },
      title: 'Alright, let\'s get your migration started! To begin, select the hosting service for which you' +
        ' want us to place the migrated content.',
    },
    step_two: {
      model: {
        previous_provider: '',
        cp_url: '',
        cp_type: '',
        hostname: '',
        username: '',
        password: '',
        notes: '',
        thirdpartymail: 0
      },
      cp_items: [
        {
          name: 'cPanel/WHM',
          value: 'cPanel'
        },
        {
          name: 'Plesk',
          value: 'Plesk'
        },
        {
          name: 'Helm',
          value: 'Helm'
        },
        {
          name: 'Unsure',
          value: 'Unsure'
        }
      ],
    },
    rules: {
      provider: {
        required: false
      },
      cp_url: {
        required: false,
        url: false
      },
      cp_type: {
        required: false
      },
      hostname: {
        required: false
      },
      username: {
        required: false
      },
      password: {
        required: false
      }
    },
    loading: false,
  }),
  methods: {
    ...mapActions(['reportError', 'reportSuccess']),
    resetFocus,
    async submitForm () {
      const valid = await this.$validator.validateAll()
      if (!valid) {
        return
      }
      this.loading = true
      const data = {
        ...this.step_one.model,
        ...this.step_two.model,
      }
      try {
        const response = await amapi.makecall('migration_request', data)
        // TODO: response.reference contains ticket ID

        this.step_counter = 3
        this.$router.push({ name: 'ticket_view', params: { ticketid: response.reference } })
        this.reportSuccess('Thank you! Your Migration Request has been received and will be processed shortly.')
      } catch (e) {
        this.reportError(e)
      }
      this.loading = false
    },
    async moveToStep (step, scope = null) {
      const valid = await this.$validator.validateAll(scope)
      if (!valid) { return }
      this.step_counter = step
    },
    resetStepper () {
      this.step_one.model.hosting_id = ''
      this.step_two.model = {
        previous_provider: '',
        cp_url: '',
        cp_type: '',
        hostname: '',
        username: '',
        password: '',
        notes: '',
      }
      this.step_counter = 1
      this.$nextTick(() => {
        this.$validator.reset()
      })
    },
  },
  computed: {
    ...mapGetters(['services']),
    isSerSelected () {
      return this.step_one.model.service !== ''
    },
    servicesList () {
      return this.services.filter(service => this.showGroups.indexOf(service.groupname) !== -1)
    },
  },
  mounted () {
    setTimeout(() => {
      this.resetFocus('#page-focus-target')
    }, 700)
  }
}
</script>
